import { Col, Form, Input, Row, notification } from 'antd';
import { filterDigits, localize, removeEmptyValues } from 'mixins/helpers';
import KdpRequest from 'mixins/ui/kdp-request';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import accountService from '../../../services/account';
import { getTaxPayerInfo } from '../actions';
import { IFindTaxpayerServiceProps } from '../find-taxpayer-service.type';
import { checkAuthorization } from '/react4xp/mixins/check-authorization/check-authorization';

const FindTaxpayerServiceFiz = forwardRef((props: IFindTaxpayerServiceProps, ref) => {
    const { lastName, firstName, middleName } = props.config;

    const [form] = Form.useForm();
    const [isMyLogin, setIsMyLogin] = useState<boolean>(false);
    const [isKdpPressed, setIsKdpPressed] = useState<boolean>(false);

    const formValues = Form.useWatch([], form);

    useEffect(() => {
        if (props.type === 'FL') {
            props.setSearchDisabled(true);
        }
    }, [props.type]);

    // Effect to prevent stale validation errors when field
    // becomes optional depending on another field value
    useEffect(() => {
        form.validateFields();
    }, [formValues]);

    const onFinish = (values: any) => {
        if ((props.token && props.type === 'FL') || props.type === 'LZCHP') {
            // Если авторизован
            props.setTableData(null);
            props.setTotal(null);
            props.setLoading(true);

            let secretKey = process.env.findTaxpayerSecretKey;
            let formattedValues = removeEmptyValues(Object.assign(values, { type: props.type }));

            props.setValues(formattedValues);

            if (props.type === 'LZCHP') {
                getTaxPayerInfo(formattedValues)
                    .then((value) => {
                        // Check if taxpayer type matches the selected type
                        if (value.data.taxpayers && value.data.taxpayers.length > 0) {
                            const taxpayerType = value.data.taxpayers[0].type;
                            if (props.type !== taxpayerType) {
                                notification['error']({
                                    message: localize('error', props.translations),
                                    description: localize(
                                        'find-taxpayer-service-type-not-match',
                                        props.translations
                                    ),
                                });
                                return;
                            }
                        }
                        props.setTableData(value.data.taxpayers);
                        props.setTotal(value.data.total);
                    })
                    .finally(() => props.setLoading(false));
            } else {
                let iin = '';

                if (form.getFieldValue('iin')) {
                    iin = form.getFieldValue('iin');
                } else {
                    iin = form.getFieldValue('iinBin');
                }

                accountService
                    .checkTaxpayerCodeUrl(
                        {
                            taxpayerCode: iin,
                            taxpayerType: props.type,
                        },
                        props.token
                    )
                    .then((data: any) => {
                        if (data.data.check === true) {
                            // This is my iin
                            getTaxPayerInfo(formattedValues, secretKey)
                                .then((value) => {
                                    // Check if taxpayer type matches the selected type
                                    if (value.data.taxpayers && value.data.taxpayers.length > 0) {
                                        const taxpayerType = value.data.taxpayers[0].type;
                                        if (props.type !== taxpayerType) {
                                            notification['error']({
                                                message: localize('error', props.translations),
                                                description: localize(
                                                    'find-taxpayer-service-type-not-match',
                                                    props.translations
                                                ),
                                            });
                                            return;
                                        }
                                    }
                                    props.setTableData(value.data.taxpayers);
                                    props.setTotal(value.data.total);
                                })
                                .catch((error: any) => {
                                    notification['error']({
                                        message: error.response?.data[props.language],
                                    });
                                })
                                .finally(() => props.setLoading(false));
                        } else {
                            // this is not my iin and set iSMyLogin false = KDP request true

                            if (data.data.type.length > 0) {
                                // iin found in knp
                                setIsMyLogin(data.data.check);
                                props.setSearchDisabled(true);

                                isKdpPressed &&
                                    getTaxPayerInfo(
                                        Object.assign(formattedValues, {
                                            iinBin: iin,
                                        }),
                                        secretKey
                                    )
                                        .then((value) => {
                                            // Check if taxpayer type matches the selected type
                                            if (
                                                value.data.taxpayers &&
                                                value.data.taxpayers.length > 0
                                            ) {
                                                const taxpayerType = value.data.taxpayers[0].type;
                                                if (props.type !== taxpayerType) {
                                                    notification['error']({
                                                        message: localize(
                                                            'error',
                                                            props.translations
                                                        ),
                                                        description: localize(
                                                            'find-taxpayer-service-type-not-match',
                                                            props.translations
                                                        ),
                                                    });
                                                    return;
                                                }
                                            }
                                            props.setTableData(value.data.taxpayers);
                                            props.setTotal(value.data.total);
                                        })
                                        .catch((error: any) => {
                                            notification['error']({
                                                message: localize('error', props.translations),
                                                description: JSON.stringify(
                                                    error.response?.data?.message
                                                ),
                                            });
                                        })
                                        .finally(() => props.setLoading(false));
                            } else if (data.data.type.length === 0) {
                                // not found in knp
                                notification['error']({
                                    message: localize('error', props.translations),
                                    description: localize(
                                        'finc-taxpayer-service-check-iin-bin-enterd-correclty',
                                        props.translations
                                    ),
                                });
                            }
                        }
                    })
                    .finally(() => {
                        props.setLoading(false);
                    });
            }
        } else {
            checkAuthorization(null, props.language, 'find-taxpayer', props.translations);
        }
    };

    useImperativeHandle(ref, () => ({
        clearForm() {
            form.resetFields();
            props.setTableData(null);
            props.setSearchDisabled(false);
            props.setLoading(false);
            props.setValues(null);
        },
        searchForm() {
            form.submit();
        },
    }));

    const onSuccess = ({ iin }: { iin: string }) => {
        setIsKdpPressed(true);
        props.setLoading(false);
        props.setSearchDisabled(false);
        form.setFieldValue('iinBin', iin);
    };

    return (
        <>
            <Form
                name="findTaxpayerFiz"
                onFinish={onFinish}
                form={form}
                layout="vertical"
            >
                <Row gutter={{ xs: 8, md: 16 }}>
                    {props.type === 'FL' && !isKdpPressed && (
                        <KdpRequest
                            user={props.user}
                            form={form}
                            onSuccess={onSuccess}
                            translations={props.translations}
                            language={props.language}
                            checkLoginNeedle="kdp-request-login-check-find-taxpayer"
                        />
                    )}
                    {((props.type === 'FL' && isKdpPressed) || props.type === 'LZCHP') && (
                        <Col
                            xs={24}
                            md={8}
                        >
                            <Form.Item
                                rules={[
                                    {
                                        required:
                                            props.type === 'FL'
                                                ? true
                                                : !(formValues?.lastName || formValues?.firstName),
                                        message: localize(
                                            'error.field-is-required',
                                            props.translations
                                        ),
                                    },
                                    {
                                        min: 12,
                                        message: localize(
                                            'error.iin-bin-length-12',
                                            props.translations
                                        ),
                                    },
                                ]}
                                label={props.iinBinLabel(props.type)}
                                name="iinBin"
                            >
                                <Input
                                    onChange={filterDigits(form, 'iinBin')}
                                    maxLength={12}
                                    placeholder={localize('placeholder.input', props.translations)}
                                />
                            </Form.Item>
                        </Col>
                    )}
                    {props.type === 'LZCHP' && (
                        <>
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label={lastName}
                                    name="lastName"
                                    rules={[
                                        {
                                            required: !formValues?.iinBin || formValues?.firstName,
                                            message: localize(
                                                'error.field-is-required',
                                                props.translations
                                            ),
                                        },
                                        {
                                            min: 3,
                                            message: localize(
                                                'error.unique-code-3',
                                                props.translations
                                            ),
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={localize(
                                            'placeholder.input',
                                            props.translations
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label={firstName}
                                    name="firstName"
                                    rules={[
                                        {
                                            required: !formValues?.iinBin || formValues?.lastName,
                                            message: localize(
                                                'error.field-is-required',
                                                props.translations
                                            ),
                                        },
                                        {
                                            min: 3,
                                            message: localize(
                                                'error.unique-code-3',
                                                props.translations
                                            ),
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={localize(
                                            'placeholder.input',
                                            props.translations
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Form.Item
                                    label={middleName}
                                    name="middleName"
                                    rules={[
                                        {
                                            min: 3,
                                            message: localize(
                                                'error.unique-code-3',
                                                props.translations
                                            ),
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder={localize(
                                            'placeholder.input',
                                            props.translations
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        </>
    );
});

export default FindTaxpayerServiceFiz;
